<template>
  <div id="app" class="d-flex flex-column">
    <header v-if="!isActive('/auth')" class="top-menu">
      <div class="top-menu-container">
        <div class="brand">
          <Icon class="brand-icon" icon="mdi:access-point-network" />
          <span class="brand-text">Dashboard</span>
        </div>
        
        <nav class="nav-items" v-if="user">
          <router-link 
            v-if="user && isMetroUser(user) || user?.company.showMonitoring"
            class="nav-item" 
            :class="{ 'active': isActive('/monitoramento') }" 
            to="/monitoramento"
          >
            <Icon icon="mdi:monitor-dashboard" class="nav-icon" />
            Monitoramento
          </router-link>
          <router-link 
            v-if="user && isMetroUser(user) || user?.company.showService"
            class="nav-item" 
            :class="{ 'active': isActive('/atendimento') }" 
            to="/atendimento"
          >
            <Icon icon="mdi:headset" class="nav-icon" />
            Atendimento
          </router-link>
          <router-link 
            class="nav-item" 
            :class="{ 'active': isActive('/problemas') }" 
            to="/problemas"
          >
            <Icon icon="mdi:alert" class="nav-icon" />
            Problemas
          </router-link>
          
          <!-- Menu de Configurações -->
          <div class="config-menu" @click.stop="toggleConfigMenu" ref="configMenuRef">
            <div class="nav-item config-item" 
                 :class="{ 'active': isConfigMenuOpen || isConfigActive() }">
              <Icon icon="mdi:cog" class="config-icon" />
              Configurações
              <Icon :icon="isConfigMenuOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'" class="chevron-icon" />
            </div>
            
            <!-- Dropdown -->
            <div v-if="isConfigMenuOpen" class="config-dropdown">
              <router-link 
                class="config-dropdown-item" 
                :class="{ 'active': isActive('/empresas') }" 
                to="/empresas"
                @click="closeConfigMenu"
              >
                <Icon icon="mdi:domain" class="dropdown-icon" />
                {{ isMetroUser(user) ? 'Empresas' : 'Empresa' }}
              </router-link>
              
              <router-link 
                v-if="isAdmin(user) && isMetroUser(user)"
                class="config-dropdown-item" 
                :class="{ 'active': isActive('/playbooks') }" 
                to="/playbooks/compartilhados"
                @click="closeConfigMenu"
              >
                <Icon icon="mdi:book-open-page-variant" class="dropdown-icon" />
                Playbooks
              </router-link>
              
              <router-link 
                v-if="isAdmin(user) && isMetroUser(user)"
                class="config-dropdown-item" 
                :class="{ 'active': isActive('/agendamento') }" 
                to="/agendamentos"
                @click="closeConfigMenu"
              >
                <Icon icon="mdi:calendar-clock" class="dropdown-icon" />
                Agendamentos
              </router-link>
              
              <router-link 
                v-if="isAdmin(user)"
                class="config-dropdown-item" 
                :class="{ 'active': isActive('/usuarios') }" 
                to="/usuarios"
                @click="closeConfigMenu"
              >
                <Icon icon="mdi:account-multiple" class="dropdown-icon" />
                Usuários
              </router-link>
            </div>
          </div>
        </nav>
        
        <div class="user-menu" v-if="user">
          <button class="logout-btn d-flex gap-2" @click="logout()">
            Sair
            <Icon icon="mdi:logout" />
          </button>
        </div>
      </div>
    </header>

    <div id="aligner" class="d-flex">
    <div id="main-aligner" class="main-content d-flex flex-column align-items-center w-100">
      <main>
        <div v-if="route.path !== '/auth/login'" class="main-content-card">
          <router-view :key="String(route.params.groups)"></router-view>
        </div>
        
        <router-view v-else></router-view>
        
        <div v-if="isLoading" class="overlay">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      </main>
    </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, computed, nextTick, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { LoadingService } from './services/LoadingService'
import { authService } from './services/backend/authService'
import { ErrorCode } from '../../zabbix-shared/enums/error'
import { IUser } from '../../zabbix-shared/interfaces/intefaces'
import { Icon } from '@iconify/vue'
import UserService from './services/OnlineUserService'
import { isAdmin, isMetroUser } from '../../zabbix-shared/utils/user'

const route = useRoute()
const router = useRouter()

const user = ref<IUser | null>(null)
const isConfigMenuOpen = ref(false)
const configMenuRef = ref<HTMLElement | null>(null)

const isActive = (basePath: string) => {
  const firstSegment = route.path.split('/')[1]
  return `/${firstSegment}` === basePath
}

const isConfigActive = () => {
  return isActive('/empresas') || isActive('/playbooks') || isActive('/agendamento') || isActive('/usuarios')
}

const isLoading = computed(() => LoadingService.isLoading)

const toggleConfigMenu = () => {
  isConfigMenuOpen.value = !isConfigMenuOpen.value
}

const closeConfigMenu = () => {
  isConfigMenuOpen.value = false
}

const handleClickOutside = (event: MouseEvent) => {
  if (configMenuRef.value && !configMenuRef.value.contains(event.target as Node)) {
    closeConfigMenu()
  }
}

const checkToken = async () => {
  try {
    await authService.getSilentToken()
    user.value = await authService.getLoggedUser();

    UserService.setUser(user.value);
    
    if(route.path == "/auth/login"){
        router.push('/empresas');
    }

    } catch (error: any) {
      const errorCode = error?.response?.data?.error
      if (errorCode === ErrorCode.NOT_AUTHENTICATED) {
        router.push('/auth/login')
      }
    }
  }


const logout = () => {
  authService.logout()
}

onMounted(async () => {
  await checkToken()
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
<style scoped>
.spinner-border {
  position: fixed;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3em;
}

main {
  width: min(1200px, 100%);
}

#main-aligner {
  overflow: auto;
}

.active {
  text-decoration: underline;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 10px 18px;
  border-radius: 8px;
  margin-bottom: 6px;
  font-size: 1.08rem;
  transition: background 0.18s;
}

.nav-link.active, .nav-link:hover {
  background: #0078d4 !important;
  color: #fff !important;
  text-decoration: none;
}

.main-content {
  background: linear-gradient(135deg, #f4f7fa 0%, #e0e7ff 100%);
}

.main-content-card {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.10);
  padding: 16px 32px 32px 32px;
  margin: 0 auto 0 auto;
  min-width: 320px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.btn-primary, .btn-success, .btn-light, .btn-danger {
  font-weight: 600;
  letter-spacing: 0.5px;
  border-radius: 8px;
  padding: 10px 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07);
  transition: background 0.18s, color 0.18s, box-shadow 0.18s;
}

.btn-primary {
  background: linear-gradient(90deg, #0078d4 60%, #00c6fb 100%);
  border: none;
  color: #fff;
}
.btn-primary:hover, .btn-primary:focus {
  background: linear-gradient(90deg, #005fa3 60%, #00a6d4 100%);
  color: #fff;
  box-shadow: 0 4px 16px 0 rgba(0,120,212,0.12);
}

.btn-success {
  background: linear-gradient(90deg, #28a745 60%, #6ee7b7 100%);
  border: none;
  color: #fff;
}
.btn-success:hover, .btn-success:focus {
  background: linear-gradient(90deg, #218838 60%, #34d399 100%);
  color: #fff;
  box-shadow: 0 4px 16px 0 rgba(40,167,69,0.12);
}

.btn-light {
  background: #fff;
  color: #181c2f;
  border: 1px solid #e0e7ff;
}
.btn-light:hover, .btn-light:focus {
  background: #f4f7fa;
  color: #0078d4;
  box-shadow: 0 4px 16px 0 rgba(31,38,135,0.08);
}

.btn-danger {
  background: linear-gradient(90deg, #dc3545 60%, #ffb4b4 100%);
  border: none;
  color: #fff;
}
.btn-danger:hover, .btn-danger:focus {
  background: linear-gradient(90deg, #b21f2d 60%, #ff6b6b 100%);
  color: #fff;
  box-shadow: 0 4px 16px 0 rgba(220,53,69,0.12);
}

.config-menu {
  position: relative;
}

.config-item {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.config-icon {
  font-size: 18px;
}

.chevron-icon {
  font-size: 16px;
  transition: transform 0.2s ease;
}

.config-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #002554;
  border: 1px solid rgba(100, 181, 246, 0.3);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  z-index: 1001;
  min-width: 200px;
  overflow: hidden;
  animation: dropdownFadeIn 0.2s ease;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.config-dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: #e3f2fd;
  text-decoration: none;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 500;
}

.config-dropdown-item:last-child {
  border-bottom: none;
}

.config-dropdown-item:hover {
  background: rgba(100, 181, 246, 0.2);
  color: #ffffff;
  text-decoration: none;
}

.config-dropdown-item.active {
  background: rgba(100, 181, 246, 0.3);
}

.dropdown-icon {
  font-size: 18px;
  color: inherit;
}

.top-menu {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: #002554;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  height: 64px;
}

.top-menu-container {
  margin: 0 auto;
  padding: 0 24px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #ffffff;
  font-size: 18px;
}

.brand-icon {
  font-size: 28px;
  color: #ffffff;
}

.brand-text {
  color: #ffffff;
}

.nav-items {
  display: flex;
  align-items: center;
  gap: 32px;
}

.nav-item {
  color: #e3f2fd;
  text-decoration: none;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-icon {
  font-size: 18px;
  color: inherit;
}

.nav-item:hover {
  color: #ffffff;
  background: rgba(100, 181, 246, 0.2);
  text-decoration: none;
}

.nav-item.active {
  background: rgba(100, 181, 246, 0.3);
}

.nav-item.active::after {
  content: '';
  position: absolute;
  bottom: -12px;
  left: 16px;
  right: 16px;
  height: 2px;
  background: #64b5f6;
}

.user-menu {
  display: flex;
  align-items: center;
  gap: 16px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e3f2fd;
}

.user-icon {
  font-size: 24px;
  color: #bbdefb;
}

.user-name {
  font-weight: 600;
  color: #e3f2fd;
}

.logout-btn {
  background: none;
  border: none;
  padding: 8px;
  border-radius: 8px;
  color: #e3f2fd;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logout-btn:hover {
  background: rgba(239, 83, 80, 0.2);
  color: #ef5350;
}

.logout-btn svg {
  font-size: 20px;
}

#aligner {
  height: 100%;
  overflow: hidden;
}


@media (max-width: 1300px) {
  .top-menu-container {
    padding: 0 16px;
  }
  
  .nav-items {
    gap: 16px;
  }
  
  .nav-item {
    padding: 6px 12px;
    font-size: 14px;
  }
  
  /* Esconder textos dos itens principais, mantendo apenas ícones */
  .nav-item:not(.config-item) {
    font-size: 0;
    padding: 8px;
  }
  
  .nav-item:not(.config-item) .nav-icon {
    font-size: 20px;
  }
  
  /* Esconder texto do menu configurações também */
  .config-item {
    font-size: 0;
    padding: 8px;
    gap: 0;
  }
  
  .config-item .config-icon {
    font-size: 20px;
  }
  
  .config-item .chevron-icon {
    display: none;
  }
  
  .brand-text {
    display: none;
  }
  
  .user-name {
    display: none;
  }
}

@media (max-width: 900px) {
  .main-content-card {
    padding: 18px 6px;
    min-width: unset;
    max-width: 98vw;
  }
}
</style>

<style>
#app {
  height: 100vh;
}

.icon {
  font-size: 28px;
}

.title {
  display: flex;
  justify-content: center !important;
  align-items: center !important;
  font-size: 16px;
  background: #0d6efd;
  color: white;
  height: 45px;
  text-transform: uppercase;
  margin-bottom: 1em;
  border-radius: 5px
}

.subtitle {
  font-size: 14px;
  height: 40px;
  background: #949494;
}

.uppercase {
  text-transform: uppercase;
}

fieldset button {
  margin-top: 20px;
  margin-bottom: 15px
}

fieldset table {
  margin-bottom: 0px !important;
}

fieldset {
  margin-bottom: 20px !important;
}
</style>